import React from "react";
import { Skeleton } from "../ui";

interface EmpathyMappingSkeletonProps {
  activeTab?: string;
}

const TAB_LABEL_WIDTHS = [70, 80, 65, 75];

/** Barra de título tipo "pill", h-[52px] fixo — mesmo padrão de todas as abas da página real. */
const PillHeaderSkeleton: React.FC<{
  titleWidth?: number;
  withButton?: boolean;
}> = ({ titleWidth = 180, withButton }) => (
  <div className="shrink-0 flex items-center justify-between gap-3 flex-wrap h-[52px] bg-white/70 backdrop-blur-xs border border-purple-200 rounded-2xl px-4 sm:px-5 py-2.5 shadow-xs">
    <div className="flex items-center gap-2">
      <Skeleton variant="circular" width={20} height={20} />
      <Skeleton
        variant="rectangular"
        width={titleWidth}
        height={16}
        className="rounded-md"
      />
    </div>
    {withButton && (
      <Skeleton
        variant="rectangular"
        width={130}
        height={32}
        className="rounded-lg"
      />
    )}
  </div>
);

/** Card genérico do padrão da página (ícone em caixa + título, corpo com linhas). */
const DimensionCardSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div className="relative overflow-hidden bg-white border border-purple-200 rounded-4xl p-5 shadow-xl shadow-gray-200/10">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton
        variant="rectangular"
        width={36}
        height={36}
        className="rounded-xl"
      />
      <Skeleton
        variant="rectangular"
        width={110}
        height={9}
        className="rounded-sm"
      />
    </div>
    <div className="space-y-2.5">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          width={`${90 - i * 10}%`}
          height={9}
          className="rounded-sm"
        />
      ))}
    </div>
  </div>
);

const SectionCardSkeleton: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-4 sm:p-5">
    <div className="flex items-start gap-3 border-b pb-3 border-purple-100 mb-4">
      <Skeleton
        variant="rectangular"
        width={40}
        height={40}
        className="rounded-xl"
      />
      <div className="flex-1 space-y-1.5">
        <Skeleton
          variant="rectangular"
          width={160}
          height={11}
          className="rounded-sm"
        />
        <Skeleton
          variant="rectangular"
          width={220}
          height={9}
          className="rounded-sm"
        />
      </div>
    </div>
    {children}
  </div>
);

const DiscoverySkeleton: React.FC = () => (
  <div className="flex flex-col gap-2 h-[calc(100vh-206px)] min-h-[360px]">
    <PillHeaderSkeleton titleWidth={220} />

    <div className="flex-1 min-h-0 border border-purple-200 rounded-3xl overflow-hidden flex flex-col bg-white shadow-xl shadow-gray-200/20">
      <div className="flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6 bg-linear-to-b from-purple-50/50 to-white overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`flex items-end gap-3 ${i % 2 === 0 ? "flex-row-reverse" : "flex-row"}`}
          >
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              className="shrink-0"
            />
            <div
              className={`space-y-2 w-full max-w-[70%] flex flex-col ${i % 2 === 0 ? "items-end" : "items-start"}`}
            >
              <Skeleton
                variant="rectangular"
                width="100%"
                height={i % 2 === 0 ? 60 : 100}
                className={`rounded-2xl ${i % 2 === 0 ? "rounded-br-none" : "rounded-bl-none"}`}
              />
              <Skeleton
                variant="text"
                width={40}
                height={9}
                className="rounded-md"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="shrink-0 p-4 border-t border-purple-200 flex gap-3 bg-white">
        <Skeleton variant="rectangular" className="flex-1 h-12 rounded-xl" />
        <Skeleton
          variant="rectangular"
          width={80}
          height={48}
          className="rounded-xl"
        />
      </div>
    </div>
  </div>
);

const MapeamentoSkeleton: React.FC = () => (
  <div className="space-y-2">
    <PillHeaderSkeleton titleWidth={140} withButton />

    {/* Perfil Estratégico do ICP */}
    <div className="bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-4 sm:p-5">
      <div className="flex items-start gap-3 border-b pb-3 border-purple-100 mb-4">
        <Skeleton
          variant="rectangular"
          width={40}
          height={40}
          className="rounded-xl"
        />
        <div className="flex-1 space-y-1.5">
          <Skeleton
            variant="rectangular"
            width={180}
            height={11}
            className="rounded-sm"
          />
          <Skeleton
            variant="rectangular"
            width={220}
            height={9}
            className="rounded-sm"
          />
        </div>
        <div className="flex gap-2">
          <Skeleton
            variant="rectangular"
            width={80}
            height={40}
            className="rounded-full"
          />
          <Skeleton
            variant="rectangular"
            width={80}
            height={40}
            className="rounded-full"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <DimensionCardSkeleton key={i} lines={3} />
        ))}
      </div>
    </div>

    {/* Mapa de Empatia (6 dimensões) */}
    <SectionCardSkeleton>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <DimensionCardSkeleton key={i} lines={2} />
        ))}
      </div>
    </SectionCardSkeleton>

    {/* Círculos de Influência (5 fatores) */}
    <SectionCardSkeleton>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <DimensionCardSkeleton key={i} lines={2} />
        ))}
      </div>
    </SectionCardSkeleton>

    {/* Prioridades Estratégicas (dores, metas, decisão) */}
    <SectionCardSkeleton>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <DimensionCardSkeleton key={i} lines={3} />
        ))}
      </div>
    </SectionCardSkeleton>
  </div>
);

const EstrategiaSkeleton: React.FC = () => (
  <div className="space-y-2">
    <PillHeaderSkeleton titleWidth={220} withButton />

    {/* ICP Insights */}
    <SectionCardSkeleton>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <DimensionCardSkeleton key={i} lines={2} />
        ))}
      </div>
    </SectionCardSkeleton>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      {/* Protocolo de Engajamento */}
      <div className="lg:col-span-8">
        <SectionCardSkeleton>
          <div className="space-y-2.5">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height={44}
                className="rounded-xl"
              />
            ))}
          </div>
        </SectionCardSkeleton>
      </div>

      {/* Gerar Post com ICP */}
      <div className="lg:col-span-4 bg-white border border-purple-200 rounded-3xl shadow-xl shadow-gray-200/20 p-4 sm:p-5">
        <div className="flex items-start gap-3 border-b pb-3 border-purple-100 mb-4">
          <Skeleton
            variant="rectangular"
            width={40}
            height={40}
            className="rounded-xl"
          />
          <div className="flex-1 space-y-1.5">
            <Skeleton
              variant="rectangular"
              width={130}
              height={11}
              className="rounded-sm"
            />
            <Skeleton
              variant="rectangular"
              width={160}
              height={9}
              className="rounded-sm"
            />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={70}
            className="rounded-xl"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={38}
            className="rounded-xl"
          />
        </div>
      </div>
    </div>
  </div>
);

const LeadsSkeleton: React.FC = () => (
  <div className="flex flex-col gap-2 h-[calc(100vh-206px)] min-h-[360px]">
    <PillHeaderSkeleton titleWidth={110} withButton />

    <div className="flex-1 min-h-0 flex flex-col gap-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="bg-white border border-purple-200 rounded-2xl p-3 flex items-center gap-3"
        >
          <Skeleton
            variant="rectangular"
            width={44}
            height={44}
            className="rounded-xl"
          />
          <div className="flex-1 space-y-1.5">
            <Skeleton
              variant="rectangular"
              width="50%"
              height={10}
              className="rounded-sm"
            />
            <Skeleton
              variant="rectangular"
              width="70%"
              height={9}
              className="rounded-sm"
            />
          </div>
          <div className="w-20 space-y-1">
            <Skeleton
              variant="rectangular"
              width="100%"
              height={6}
              className="rounded-full"
            />
            <Skeleton
              variant="rectangular"
              width={40}
              height={8}
              className="rounded-sm ml-auto"
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const EmpathyMappingSkeleton: React.FC<EmpathyMappingSkeletonProps> = ({
  activeTab = "discovery",
}) => {
  return (
    <div>
      {/* Navbar de tabs - mesmo controle segmentado da página real */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 bg-white/95 backdrop-blur-md border-b border-purple-200">
        <div className="w-full flex gap-1 bg-gray-100 p-1">
          {TAB_LABEL_WIDTHS.map((w, i) => (
            <div
              key={i}
              className="flex-1 h-8 flex items-center justify-center gap-1.5 rounded-lg bg-white border border-gray-200"
            >
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton
                variant="rectangular"
                width={w}
                height={10}
                className="rounded-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-full mt-2">
        {activeTab === "discovery" && <DiscoverySkeleton />}
        {activeTab === "mapeamento" && <MapeamentoSkeleton />}
        {activeTab === "estrategia" && <EstrategiaSkeleton />}
        {activeTab === "leads" && <LeadsSkeleton />}
      </div>
    </div>
  );
};

export default EmpathyMappingSkeleton;
